import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Portal,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Collapse,
    CircularProgress,
    Tooltip,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import DateRangeIcon from '@mui/icons-material/DateRange';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import EnumsService from '../../../Services/enums.service';
import ReportsService from '../../../Services/reports.service';
import Constants from '../../../assests/Constants/constants';
import { beautifyNumber } from '../../../Utils/FormatNumber';
import { buildProfitAndLossCardsModel, buildTransactionsByCard } from '../../../Utils/profitAndLossCardsReport';
import buildProfitAndLossCardsReportHtml from './profitAndLossCardsReportHtml';
import downloadProfitAndLossCardsReportExcel from './profitAndLossCardsReportExcel';
import FilterMonthYearPicker from '../../Widgets/CustomDates/FilterMonthYearPicker/FilterMonthYearPicker';
import CustomYearDatePicker from '../../Widgets/CustomDates/CustomYearDatePicker/CustomYearDatePicker';
import './ProfitAndLossCardsReport.css';
import styles from './ProfitAndLossCardsReport.module.scss';

/** FetchType.All on the backend: every account card of the case. */
const FETCH_TYPE_ALL = 2;

/**
 * Month periods are anchored on day 10 UTC and year periods on 10-Jan .. 31-Dec, matching
 * AccountCardDashboard. The day-10 anchor keeps `toISOString()` inside the selected month
 * regardless of the browser timezone.
 */
const monthAnchor = (year, month) => new Date(Date.UTC(year, month, 10, 0, 0, 0, 0));
const yearStartAnchor = (year) => new Date(Date.UTC(year, 0, 10, 0, 0, 0, 0));
const yearEndAnchor = (year) => new Date(Date.UTC(year, 11, 31, 0, 0, 0, 0));

const toUtcDate = (value) => {
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

const defaultFormatDate = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    const day = `0${date.getDate()}`.slice(-2);
    const month = `0${date.getMonth() + 1}`.slice(-2);
    return `${day}/${month}/${date.getFullYear()}`;
};

/**
 * Profit & Loss report over account cards, showing the detailed transactions of every
 * card. Sections and ordering come from the same trialBalanceDetails call the Profit &
 * Loss report of LedgerReport uses, so both reports read identically.
 */
const ProfitAndLossCardsReport = ({
    open,
    handleClose,
    client,
    token,
    startDate,
    endDate,
    isMonthly = true,
    onlyProcessed = false,
    filterByDocumentDate = false,
    direction = 'rtl',
    formatDate = defaultFormatDate,
    /**
     * Opens a single ledger line in the dashboard's EditModal. The report stays open and
     * EditModal layers on top; closing EditModal does not touch the report.
     */
    handleTransactionEdit,
    /** Bumped by the dashboard after an EditModal save so the report reloads in place. */
    reloadToken = 0,
}) => {
    const { t } = useTranslation();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [exportError, setExportError] = useState('');
    const [trialBalanceData, setTrialBalanceData] = useState(null);
    const [transactionsByCard, setTransactionsByCard] = useState(new Map());

    const [showZeros, setShowZeros] = useState(false);
    const [showPercentage, setShowPercentage] = useState(true);
    const [showAccountCodeId, setShowAccountCodeId] = useState(true);
    const [expandedCards, setExpandedCards] = useState(() => new Set());
    const [downloading, setDownloading] = useState(false);

    /**
     * The period the report is rendered for. It is seeded from the dashboard filter bar and
     * can then be changed from inside the modal without touching the dashboard.
     */
    const [range, setRange] = useState(null);

    useEffect(() => {
        if (!open) return;
        const start = toUtcDate(startDate);
        const end = toUtcDate(endDate);
        if (!start || !end) return;
        setRange({ start, end, isMonthly });
    }, [open, startDate, endDate, isMonthly]);

    const loadReport = useCallback(async () => {
        if (!client?.caseId || !client?.accountantId || !token || !range?.start || !range?.end) return;

        const { start, end, isMonthly: monthly } = range;

        setLoading(true);
        setError('');
        try {
            const [trialBalanceResponse, cardDetailsResponse] = await Promise.all([
                // Section order, grouping and balances: identical call to the P&L report.
                ReportsService.GetTrialBalanceDetails(
                    client.caseId,
                    client.accountantId,
                    start,
                    end,
                    !monthly,
                    token,
                    onlyProcessed,
                    true,
                    null,
                    filterByDocumentDate
                ),
                // Document-level ledger lines for every result-account (תוצאתי) card.
                EnumsService.AccountCardPalCodeDetailsWithFetchType(
                    [],
                    [],
                    [],
                    client.accountantId,
                    client.caseId,
                    start,
                    end,
                    !monthly,
                    onlyProcessed,
                    FETCH_TYPE_ALL,
                    token,
                    filterByDocumentDate,
                    null,
                    [Constants.ResultAccountName]
                ),
            ]);

            // trialBalanceDetails answers 404 for a period without any trial balance, which
            // the service reports as a missing response. That is an empty report, not a failure.
            if (!trialBalanceResponse?.ok) {
                console.warn('No trial balance returned for the selected period.');
            }

            setTrialBalanceData(trialBalanceResponse?.ok ? await trialBalanceResponse.json() : null);
            setTransactionsByCard(
                cardDetailsResponse?.ok
                    ? buildTransactionsByCard(await cardDetailsResponse.json())
                    : new Map()
            );
        } catch (err) {
            console.error('Failed to load the profit and loss cards report:', err);
            setError(t('ErrorFetchReportCardDataReturned'));
            setTrialBalanceData(null);
            setTransactionsByCard(new Map());
        } finally {
            setLoading(false);
        }
    }, [client, token, range, onlyProcessed, filterByDocumentDate, t]);

    // Reset the expanded cards only when the dialog transitions to open. Keeping this out of
    // the load effect means a reload triggered by editing/saving a transaction on the
    // dashboard (which can change the applied period) leaves the currently expanded cards
    // open, so the user can keep opening more transactions.
    useEffect(() => {
        if (open) setExpandedCards(new Set());
    }, [open]);

    useEffect(() => {
        if (!open) return;
        loadReport();
    }, [open, loadReport, reloadToken]);

    const model = useMemo(
        () => buildProfitAndLossCardsModel({ trialBalanceData, transactionsByCard, showZeros, t }),
        [trialBalanceData, transactionsByCard, showZeros, t]
    );

    const allCardKeys = useMemo(
        () => model.sections.flatMap((section) => section.palCodes.flatMap(
            (palCode) => palCode.cards.filter((card) => card.transactions.length).map((card) => card.key)
        )),
        [model]
    );

    const columnsCount = 2 + (showPercentage ? 1 : 0);

    const detailColumns = useMemo(() => [
        { key: 'creditAccountName', label: t('Credit Account') },
        { key: 'debitAccountName', label: t('Debit Account') },
        { key: 'debitAccount', label: t('Debit'), amount: true },
        { key: 'creditAccount', label: t('Credit'), amount: true },
        { key: 'balance', label: t('Balance'), amount: true },
        { key: 'uploadDate', label: t('ValueDate'), date: true },
        { key: 'documentDate', label: t('ReferenceDate'), date: true },
        { key: 'reference1', label: t('Reference1') },
        { key: 'reference2', label: t('Reference2') },
        // Descriptions are stored as translation keys for system-generated rows.
        { key: 'description', label: t('Details'), translate: true },
    ], [t]);

    const toggleCard = (cardKey) => {
        setExpandedCards((previous) => {
            const next = new Set(previous);
            if (next.has(cardKey)) next.delete(cardKey);
            else next.add(cardKey);
            return next;
        });
    };

    const cardLabel = (card) => {
        const name = card.accountCardName || '';
        return showAccountCodeId && card.accountCardId ? `${card.accountCardId} ${name}`.trim() : name;
    };

    /**
     * Derived from the selected range rather than from the server response, so the header
     * matches the date selectors immediately and stays right for periods that hold no data.
     */
    const period = useMemo(() => {
        if (!range?.start || !range?.end) return '';

        const label = (date) => {
            const month = `0${date.getUTCMonth() + 1}`.slice(-2);
            return range.isMonthly ? `${month}/${date.getUTCFullYear()}` : `${date.getUTCFullYear()}`;
        };

        const from = label(range.start);
        const to = label(range.end);
        return from === to ? from : `${from} - ${to}`;
    }, [range]);

    /** Month/year mode switch. Yearly collapses the range onto the selected start year. */
    const handleRangeModeChange = (monthly) => {
        setRange((previous) => {
            if (!previous || previous.isMonthly === monthly) return previous;
            if (monthly) {
                return {
                    isMonthly: true,
                    start: monthAnchor(previous.start.getUTCFullYear(), 0),
                    end: monthAnchor(previous.end.getUTCFullYear(), 11),
                };
            }
            const year = previous.start.getUTCFullYear();
            return { isMonthly: false, start: yearStartAnchor(year), end: yearEndAnchor(year) };
        });
    };

    /** Month pickers change one part at a time and keep the other, like the filter bar. */
    const handleMonthlyPartChange = (edge, value, part) => {
        const picked = toUtcDate(value);
        if (!picked) return;

        setRange((previous) => {
            if (!previous) return previous;
            const current = previous[edge];
            const year = part === 'year' ? picked.getUTCFullYear() : current.getUTCFullYear();
            const month = part === 'month' ? picked.getUTCMonth() : current.getUTCMonth();
            return { ...previous, [edge]: monthAnchor(year, month) };
        });
    };

    const handleYearlyChange = (edge, value) => {
        const picked = toUtcDate(value);
        if (!picked) return;

        setRange((previous) => {
            if (!previous) return previous;
            const year = picked.getUTCFullYear();
            return { ...previous, [edge]: edge === 'start' ? yearStartAnchor(year) : yearEndAnchor(year) };
        });
    };

    const handleDownloadPdf = async () => {
        if (!model.sections.length) return;
        setDownloading(true);
        setExportError('');
        try {
            const html = buildProfitAndLossCardsReportHtml({
                model,
                title: t('ProfitAndLossCardsReport'),
                clientName: trialBalanceData?.clientName || client?.name,
                clientCase: trialBalanceData?.clientCase || client?.caseId,
                period,
                showPercentage,
                showAccountCodeId,
                showTransactions: true,
                formatDate,
                t,
            });

            const { blob, fileName } = await ReportsService.generatePdfFromHtml(html, client.caseId, token, {
                fileName: `${t('ProfitAndLossCardsReport')}.pdf`,
            });

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName || `${t('ProfitAndLossCardsReport')}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Failed to export the profit and loss cards report:', err);
            setExportError(t('ErrorGeneratingPdf'));
        } finally {
            setDownloading(false);
        }
    };

    const handleDownloadExcel = () => {
        if (!model.sections.length) return;
        setExportError('');
        try {
            downloadProfitAndLossCardsReportExcel({
                model,
                client,
                trialBalanceData,
                title: t('ProfitAndLossCardsReport'),
                period,
                showPercentage,
                showAccountCodeId,
                formatDate,
                t,
            });
        } catch (err) {
            console.error('Failed to export the profit and loss cards report to Excel:', err);
            setExportError(t('ErrorGeneratingExcel'));
        }
    };

    const renderAmountCells = (value, percentage) => (
        <>
            <TableCell className="pl-cards-report-amount">{beautifyNumber(value)}</TableCell>
            {showPercentage && <TableCell className="pl-cards-report-amount">{percentage}</TableCell>}
        </>
    );

    /**
     * Double-click a ledger line to open it in the dashboard's EditModal, mirroring the
     * account-card collapsible table. `type1` + `id` are all the parent needs to fetch the
     * full document. System-generated rows without a real document type are left inert.
     *
     * The report stays open and EditModal layers on top (see `disableEnforceFocus` on the
     * Dialog below, which lets EditModal's inputs receive focus). Closing EditModal does not
     * touch the report.
     */
    const handleTransactionDoubleClick = useCallback((transaction) => {
        if (!handleTransactionEdit || !transaction?.id || !transaction?.type1) return;
        handleTransactionEdit(transaction);
    }, [handleTransactionEdit]);

    const isTransactionClickable = (transaction) => Boolean(
        handleTransactionEdit && transaction?.id && transaction?.type1
    );

    const renderTransactions = (card) => (
        <TableRow>
            <TableCell className="pl-cards-report-detail-cell" colSpan={columnsCount}>
                <Collapse in={expandedCards.has(card.key)} timeout="auto" unmountOnExit>
                    <div className="pl-cards-report-detail-wrapper">
                        <div className="pl-cards-report-detail-title">
                            {`${t('Transactions')} (${card.transactions.length})`}
                        </div>
                        <TableContainer>
                            <Table size="small" className="pl-cards-report-detail-table" dir={direction}>
                                <TableHead>
                                    <TableRow>
                                        {detailColumns.map((column) => (
                                            <TableCell
                                                key={column.key}
                                                className={column.amount ? 'pl-cards-report-amount' : ''}
                                            >
                                                {column.label}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {card.transactions.map((transaction, index) => {
                                        const clickable = isTransactionClickable(transaction);
                                        return (
                                        <TableRow
                                            key={transaction.id || `${card.key}_${index}`}
                                            hover={clickable}
                                            onDoubleClick={clickable ? () => handleTransactionDoubleClick(transaction) : undefined}
                                            title={clickable ? t('ViewTransaction', { defaultValue: 'View Transaction' }) : undefined}
                                            sx={clickable ? {
                                                cursor: 'pointer',
                                                '&:hover': { backgroundColor: 'rgba(48, 79, 255, 0.08)' },
                                            } : undefined}
                                        >
                                            {detailColumns.map((column) => {
                                                const value = transaction[column.key];
                                                if (column.amount) {
                                                    return (
                                                        <TableCell key={column.key} className="pl-cards-report-amount">
                                                            {beautifyNumber(value)}
                                                        </TableCell>
                                                    );
                                                }
                                                if (column.date) {
                                                    return (
                                                        <TableCell key={column.key}>
                                                            {value ? formatDate(value) : '-'}
                                                        </TableCell>
                                                    );
                                                }
                                                if (!value) {
                                                    return <TableCell key={column.key}>-</TableCell>;
                                                }
                                                return (
                                                    <TableCell key={column.key}>
                                                        {column.translate ? t(value, { defaultValue: value }) : value}
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                        );
                                    })}
                                    <TableRow className="pl-cards-report-detail-total">
                                        <TableCell colSpan={2}>{t('Total')}</TableCell>
                                        <TableCell className="pl-cards-report-amount">
                                            {beautifyNumber(card.transactions.reduce(
                                                (sum, transaction) => sum + (Number(transaction.debitAccount) || 0), 0
                                            ))}
                                        </TableCell>
                                        <TableCell className="pl-cards-report-amount">
                                            {beautifyNumber(card.transactions.reduce(
                                                (sum, transaction) => sum + (Number(transaction.creditAccount) || 0), 0
                                            ))}
                                        </TableCell>
                                        <TableCell className="pl-cards-report-amount">
                                            {beautifyNumber(card.transactionsTotal)}
                                        </TableCell>
                                        <TableCell colSpan={detailColumns.length - 5} />
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </div>
                </Collapse>
            </TableCell>
        </TableRow>
    );

    const renderSection = (section) => (
        <div className="pl-cards-report-section" key={section.key}>
            <div className="pl-cards-report-section-header">
                {section.showAgLabel && <div className="pl-cards-report-ag-label">{section.agLabel}</div>}
                <div className="pl-cards-report-detail-label">{section.detailLabel}</div>
            </div>
            <TableContainer>
                <Table size="small" className="pl-cards-report-table" dir={direction}>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('AccountCard')}</TableCell>
                            <TableCell className="pl-cards-report-amount">{t('Balance')}</TableCell>
                            {showPercentage && (
                                <TableCell className="pl-cards-report-amount">{t('PercentOfSales')}</TableCell>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {section.palCodes.map((palCode) => (
                            <React.Fragment key={palCode.key}>
                                <TableRow className="pl-cards-report-palcode-row">
                                    <TableCell colSpan={columnsCount}>{palCode.palCodeLabel}</TableCell>
                                </TableRow>

                                {palCode.cards.map((card) => (
                                    <React.Fragment key={card.key}>
                                        <TableRow
                                            className="pl-cards-report-card-row"
                                            hover
                                            onClick={() => card.transactions.length && toggleCard(card.key)}
                                        >
                                            <TableCell>
                                                {card.transactions.length > 0 && (
                                                    <IconButton size="small" sx={{ mr: 0.5 }}>
                                                        {expandedCards.has(card.key)
                                                            ? <KeyboardArrowUp fontSize="small" />
                                                            : <KeyboardArrowDown fontSize="small" />}
                                                    </IconButton>
                                                )}
                                                {cardLabel(card)}
                                                {card.transactions.length > 0 && (
                                                    <span style={{ color: '#8b8f9e', fontSize: 11, marginInlineStart: 6 }}>
                                                        {`(${card.transactions.length})`}
                                                    </span>
                                                )}
                                            </TableCell>
                                            {renderAmountCells(card.balance, card.percentage)}
                                        </TableRow>
                                        {card.transactions.length > 0 && renderTransactions(card)}
                                    </React.Fragment>
                                ))}

                                <TableRow className="pl-cards-report-subtotal">
                                    <TableCell>{`${t('Total')}: ${palCode.palCodeLabel}`}</TableCell>
                                    {renderAmountCells(palCode.total, palCode.percentage)}
                                </TableRow>
                            </React.Fragment>
                        ))}

                        <TableRow className="pl-cards-report-category-total">
                            <TableCell>{`${t('Total')}: ${section.categoryLabel}`}</TableCell>
                            {renderAmountCells(section.categoryTotal, section.categoryPercentage)}
                        </TableRow>

                        {section.isLastCategoryInGroup && (
                            <TableRow className="pl-cards-report-group-total">
                                <TableCell>{`${t('Total')} ${section.accountingGroupName}:`}</TableCell>
                                {renderAmountCells(section.groupTotal, section.groupPercentage)}
                            </TableRow>
                        )}

                        {section.showRunningTotal && (
                            <TableRow className="pl-cards-report-running-total">
                                <TableCell>
                                    {t('ProfitAndLossAfterGroup', { group: section.accountingGroupName })}
                                </TableCell>
                                {renderAmountCells(section.runningTotal, section.runningPercentage)}
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );

    const renderSwitch = (checked, onChange, label) => (
        <label className={styles.switchRow}>
            <span>{label}</span>
            <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
            <span className={styles.switchTrack} aria-hidden="true" />
        </label>
    );

    if (!open) return null;

    return (
        <Portal>
            <div
                className={`${styles.overlay} ${direction === 'rtl' ? styles.rtl : styles.ltr}`}
                onClick={handleClose}
            >
                <div dir={direction} onClick={(e) => e.stopPropagation()} style={{ display: 'flex', width: '100%', justifyContent: 'center' }}>
                    <div className={styles.modal}>
                        <div className={styles.body}>
                            {/* Header */}
                            <div className={styles.header}>
                                <div className={styles.headerTitles}>
                                    <h2 className={styles.title}>{t('ProfitAndLossCardsReport')}</h2>
                                    <p className={styles.subtitle}>
                                        {[
                                            trialBalanceData?.clientName || client?.name,
                                            trialBalanceData?.clientCase || client?.caseId,
                                            period,
                                        ].filter(Boolean).join(' | ')}
                                    </p>
                                </div>
                                <Tooltip title={t('Close')}>
                                    <IconButton onClick={handleClose} className={styles.closeIcon} aria-label={t('Close')}>
                                        <CancelIcon />
                                    </IconButton>
                                </Tooltip>
                            </div>

                            {/* Period / date range */}
                            <div className={styles.period}>
                                <span className={styles.periodLabel}>
                                    <DateRangeIcon sx={{ fontSize: 18 }} />
                                    {t('DateRange')}
                                </span>

                                <div className={styles.modeToggle}>
                                    <button
                                        type="button"
                                        className={`${styles.tab}${range?.isMonthly ? ` ${styles.tabActive}` : ''}`}
                                        onClick={() => handleRangeModeChange(true)}
                                    >
                                        {t('Monthly')}
                                    </button>
                                    <button
                                        type="button"
                                        className={`${styles.tab}${range && !range.isMonthly ? ` ${styles.tabActive}` : ''}`}
                                        onClick={() => handleRangeModeChange(false)}
                                    >
                                        {t('Yearly')}
                                    </button>
                                </div>

                                {range && (range.isMonthly ? (
                                    <div className={styles.dateSelectRange}>
                                        <div className={styles.dateSelect}>
                                            <FilterMonthYearPicker
                                                year={range.start}
                                                month={range.start}
                                                onYearChange={(value) => handleMonthlyPartChange('start', value, 'year')}
                                                onMonthChange={(value) => handleMonthlyPartChange('start', value, 'month')}
                                                labelYear={t('StartYear')}
                                                labelMonth={t('StartMonth')}
                                                direction={direction}
                                            />
                                        </div>
                                        <div className={styles.rangeDivider} />
                                        <div className={styles.dateSelect}>
                                            <FilterMonthYearPicker
                                                year={range.end}
                                                month={range.end}
                                                onYearChange={(value) => handleMonthlyPartChange('end', value, 'year')}
                                                onMonthChange={(value) => handleMonthlyPartChange('end', value, 'month')}
                                                labelYear={t('EndYear')}
                                                labelMonth={t('EndMonth')}
                                                direction={direction}
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <div className={styles.dateRow}>
                                        <div className={styles.dateField}>
                                            <CustomYearDatePicker
                                                date={range.start}
                                                onDateChange={(value) => handleYearlyChange('start', value)}
                                                label={t('StartDate')}
                                            />
                                        </div>
                                        <div className={styles.dateField}>
                                            <CustomYearDatePicker
                                                date={range.end}
                                                onDateChange={(value) => handleYearlyChange('end', value)}
                                                label={t('EndDate')}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Toolbar: switches + export actions */}
                            <div className={styles.toolbar}>
                                <div className={styles.switches}>
                                    {renderSwitch(showZeros, setShowZeros, t('ShowZeros'))}
                                    {renderSwitch(showPercentage, setShowPercentage, t('ShowPercentageColumn'))}
                                    {renderSwitch(showAccountCodeId, setShowAccountCodeId, t('ShowAccountCodeId'))}
                                </div>

                                <div className={styles.toolbarSpacer} />

                                <button
                                    type="button"
                                    className={styles.ghostBtn}
                                    disabled={!allCardKeys.length}
                                    onClick={() => setExpandedCards(new Set(allCardKeys))}
                                >
                                    <UnfoldMoreIcon fontSize="small" />
                                    {t('ExpandAll')}
                                </button>
                                <button
                                    type="button"
                                    className={styles.ghostBtn}
                                    disabled={!expandedCards.size}
                                    onClick={() => setExpandedCards(new Set())}
                                >
                                    <UnfoldLessIcon fontSize="small" />
                                    {t('CollapseAll')}
                                </button>
                                <button
                                    type="button"
                                    className={styles.excelBtn}
                                    disabled={loading || !model.sections.length}
                                    onClick={handleDownloadExcel}
                                >
                                    <TableChartIcon fontSize="small" />
                                    {t('DownloadExcel')}
                                </button>
                                <button
                                    type="button"
                                    className={styles.primaryBtn}
                                    disabled={downloading || loading || !model.sections.length}
                                    onClick={handleDownloadPdf}
                                >
                                    {downloading
                                        ? <CircularProgress size={14} color="inherit" />
                                        : <PictureAsPdfIcon fontSize="small" />}
                                    {t('DownloadPDF')}
                                </button>

                                {exportError && <span className={styles.exportError}>{exportError}</span>}
                            </div>

                            {/* Report body */}
                            <div className={styles.scroll}>
                                {loading && (
                                    <div className={styles.loading}>
                                        <CircularProgress className={styles.spinner} />
                                    </div>
                                )}

                                {!loading && error && <div className={styles.emptyState}>{error}</div>}

                                {!loading && !error && !model.sections.length && (
                                    <div className={styles.emptyState}>
                                        {period ? `${t('NoData')} — ${period}` : t('NoData')}
                                    </div>
                                )}

                                {!loading && !error && model.sections.map(renderSection)}

                                {!loading && !error && model.sections.length > 0 && (
                                    <div className="pl-cards-report-grand-total">
                                        <span>{t('TotalProfitAndLossInReport')}</span>
                                        <span className="pl-cards-report-amount">
                                            {beautifyNumber(model.grandTotal)}
                                            {showPercentage && `  ${model.grandTotalPercentage}`}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Portal>
    );
};

export default ProfitAndLossCardsReport;
